import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { liveRadios, type SortMode } from "@/mocks/liveRadios";
import { radioStations } from "@/mocks/radios";
import { livePrograms, type ProgramSortMode } from "@/mocks/livePrograms";
import { liveHosts, type HostSortMode } from "@/mocks/liveHosts";
import { adminLiveData, statusLabels as liveStatusLabels, statusColors as liveStatusColors, providerIcons, providerLabels, providerColors } from "@/mocks/adminLive";
import type { AdminLive, LiveStatus } from "@/mocks/adminLive";
import { useReminders } from "@/hooks/useReminders";
import { SwalAlert } from "@/hooks/useSweetAlert";

/* ── Section tabs ── */
type LiveSection = 'radios' | 'programs' | 'hosts' | 'transmisiones';

const LIVE_SECTIONS: { id: LiveSection; label: string; count: number; icon: string; color: string }[] = [
  { id: 'radios', label: 'Radios', count: liveRadios.length, icon: 'ri-radio-line', color: 'text-rose-500' },
  { id: 'programs', label: 'Programas', count: livePrograms.length, icon: 'ri-mic-line', color: 'text-emerald-500' },
  { id: 'hosts', label: 'Locutores', count: liveHosts.length, icon: 'ri-user-voice-line', color: 'text-amber-500' },
  { id: 'transmisiones', label: 'Transmisiones', count: adminLiveData.lives.length, icon: 'ri-live-line', color: 'text-red-500' },
];

/* ── Sort options ── */
const RADIO_SORTS: { key: SortMode; label: string }[] = [
  { key: 'activity', label: 'Actividad' },
  { key: 'listeners', label: 'Oyentes' },
  { key: 'interaction', label: 'Interacción' },
];

const PROGRAM_SORTS: { key: ProgramSortMode; label: string }[] = [
  { key: 'activity', label: 'Actividad' },
  { key: 'interactions', label: 'Oyentes' },
  { key: 'followers', label: 'Interacción' },
];

const HOST_SORTS: { key: HostSortMode; label: string }[] = [
  { key: 'activity', label: 'Actividad' },
  { key: 'followers', label: 'Seguidores' },
  { key: 'name', label: 'Nombre A-Z' },
];

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ── Radio Card ── */
function LiveRadioCard({ radio, onClick }: { radio: typeof liveRadios[0]; onClick: () => void }) {
  const statusBadge = radio.status === 'live-event' ? { label: 'EVENTO LIVE', bg: 'bg-rose-500' }
    : radio.status === 'live-show' ? { label: 'SHOW LIVE', bg: 'bg-violet-500' }
    : radio.status === 'special' ? { label: 'ESPECIAL', bg: 'bg-amber-500' }
    : { label: 'AL AIRE', bg: 'bg-emerald-500' };

  return (
    <button onClick={onClick} className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer group text-left w-full">
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-100">
        <img src={radio.thumbnailUrl} alt={radio.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground-950/50 backdrop-blur-sm text-white text-[9px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          LIVE
        </div>
        <div className={`absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md ${statusBadge.bg} text-white text-[8px] font-bold`}>
          {statusBadge.label}
        </div>
        <div className="absolute bottom-2.5 left-2.5 flex items-center gap-2">
          <span className="text-white text-[10px] font-semibold bg-foreground-950/40 backdrop-blur-sm px-2 py-0.5 rounded-full">{radio.upTime}</span>
          <span className="text-white text-[10px] font-semibold bg-foreground-950/40 backdrop-blur-sm px-2 py-0.5 rounded-full">
            <i className="ri-chat-1-line text-[9px] mr-0.5"></i>{formatCount(radio.chatMsgCount)}
          </span>
        </div>
      </div>
      <div className="p-3.5">
        <h3 className="text-sm font-bold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">{radio.name}</h3>
        <p className="text-[10px] text-foreground-500 mt-0.5">{radio.frequency} · {radio.category}</p>
        {radio.programs[0] && (
          <div className="flex items-center gap-2 mt-2.5 p-2 rounded-lg bg-accent-50 border border-accent-100">
            <div className="w-1.5 h-5 rounded-full bg-accent-500 shrink-0"></div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-foreground-800 truncate">{radio.programs[0].name}</p>
              <p className="text-[9px] text-foreground-500">{radio.programs[0].host}</p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-1.5 mt-2.5">
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{radio.activityScore}</div><div className="text-[9px] text-foreground-400">Actividad</div></div>
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{formatCount(radio.currentListeners)}</div><div className="text-[9px] text-foreground-400">Oyentes</div></div>
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{radio.interactionScore}</div><div className="text-[9px] text-foreground-400">Interacción</div></div>
        </div>
      </div>
    </button>
  );
}

/* ── Program Card ── */
function LiveProgramCard({ program, onClick }: { program: typeof livePrograms[0]; onClick: () => void }) {
  return (
    <button onClick={onClick} className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer group text-left w-full">
      <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-100">
        <img src={program.coverUrl} alt={program.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-[9px] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>AL AIRE</div>
      </div>
      <div className="p-3.5">
        <h3 className="text-sm font-bold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">{program.name}</h3>
        <p className="text-[10px] text-foreground-500 mt-0.5">{program.radioName} · {program.radioFrequency}</p>
        <div className="flex items-center gap-2 mt-2.5">
          <div className="w-7 h-7 rounded-full overflow-hidden bg-background-100 border border-background-200 shrink-0"><img src={program.hostPhotoUrl} alt={program.host} className="w-full h-full object-cover object-top" loading="lazy" /></div>
          <div className="min-w-0"><p className="text-[11px] font-semibold text-foreground-800 truncate">{program.host}</p><p className="text-[9px] text-foreground-500">Locutor</p></div>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mt-2.5">
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{program.activityScore}</div><div className="text-[9px] text-foreground-400">Actividad</div></div>
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{formatCount(program.followers)}</div><div className="text-[9px] text-foreground-400">Seguidores</div></div>
          <div className="text-center bg-background-100 rounded-lg py-1.5"><div className="text-xs font-bold text-foreground-800">{program.interactions}</div><div className="text-[9px] text-foreground-400">Interacción</div></div>
        </div>
      </div>
    </button>
  );
}

/* ── Host Card ── */
function LiveHostCard({ host, onClick }: { host: typeof liveHosts[0]; onClick: () => void }) {
  return (
    <button onClick={onClick} className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer group text-left w-full">
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-100">
        <img src={host.photoUrl} alt={host.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/90 text-white text-[9px] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>EN CABINA</div>
      </div>
      <div className="p-3.5">
        <h3 className="text-sm font-bold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">{host.name}</h3>
        <p className="text-[10px] text-foreground-500 mt-0.5 truncate">{host.role}</p>
        {host.programName && (
          <div className="flex items-center gap-2 mt-2.5 p-2 rounded-lg bg-accent-50 border border-accent-100"><div className="w-1.5 h-5 rounded-full bg-accent-500 shrink-0"></div><div className="min-w-0"><p className="text-[11px] font-bold text-foreground-800 truncate">{host.programName}</p><p className="text-[9px] text-foreground-500">{host.radioName} · {host.radioFrequency}</p></div></div>
        )}
        <div className="flex items-center justify-between mt-2.5"><div className="flex items-center gap-1 text-[10px] text-foreground-500"><i className="ri-user-heart-line text-xs"></i><span className="font-semibold text-foreground-700">{formatCount(host.followers)}</span></div><div className="flex items-center gap-1 text-[10px] text-foreground-500"><span className="font-semibold text-foreground-700">{host.activityScore}</span><span className="text-foreground-400">act.</span></div></div>
      </div>
    </button>
  );
}

/* ── Live Transmisión Card ── */
function LiveTransmisionCard({ live, onOpen, reminded, onToggleReminder }: {
  live: AdminLive;
  onOpen: () => void;
  reminded: boolean;
  onToggleReminder: () => void;
}) {
  const sCol = liveStatusColors[live.status];
  const pCol = providerColors[live.provider];
  const dateLabel = new Date(live.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  const isEnVivo = live.status === 'en_vivo';

  return (
    <div onClick={onOpen} className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all cursor-pointer">
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-100">
        <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
        <div className="absolute top-2.5 left-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${sCol.bg} ${sCol.text}`}>
            {isEnVivo && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>}
            <i className={`${providerIcons[live.provider]} text-[9px]`}></i>
            {liveStatusLabels[live.status]}
          </span>
        </div>
        {isEnVivo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-red-500/80 flex items-center justify-center">
              <i className="ri-play-fill text-white text-xl ml-0.5"></i>
            </div>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-xs font-bold text-foreground-950 line-clamp-2 mb-1">{live.title}</h3>
        <p className="text-[10px] text-foreground-500 truncate mb-2">{live.host}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-calendar-line text-[9px]"></i>{dateLabel}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line text-[9px]"></i>{live.time}</span>
          </div>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${pCol.bg} ${pCol.text}`}>
            <i className={`${providerIcons[live.provider]} text-[8px]`}></i>{providerLabels[live.provider]}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 px-3 pb-3" onClick={e => e.stopPropagation()}>
        <button onClick={onOpen}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-background-100 text-foreground-500 hover:bg-red-50 whitespace-nowrap cursor-pointer">
          <i className="ri-eye-line text-[10px]"></i>Ver
        </button>
        <button onClick={() => {
          const url = `${window.location.origin}/live/${live.id}`;
          if (navigator.share) navigator.share({ title: live.title, text: live.description.slice(0, 100), url });
          else navigator.clipboard.writeText(url);
        }}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-background-100 text-foreground-500 hover:bg-violet-50 whitespace-nowrap cursor-pointer">
          <i className="ri-share-forward-line text-[10px]"></i>
        </button>
        <div className="flex-1"></div>
        <button onClick={onToggleReminder}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all cursor-pointer ${
            reminded ? 'bg-amber-100 text-amber-600' : 'bg-background-100 text-foreground-400'
          }`}>
          <i className={`${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-[12px]`}></i>
        </button>
      </div>
    </div>
  );
}

/* ── Transmisiones Section ── */
function TransmisionesSection() {
  const { hasReminder, toggleReminder } = useReminders();
  const [liveStatus, setLiveStatus] = useState<'todos' | LiveStatus>('todos');
  const [selectedLive, setSelectedLive] = useState<AdminLive | null>(null);

  const lives = adminLiveData.lives;

  const filtered = useMemo(() => {
    if (liveStatus === 'todos') return lives;
    return lives.filter(l => l.status === liveStatus);
  }, [liveStatus]);

  const statusFilters: { id: string; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todas', icon: 'ri-grid-line' },
    { id: 'en_vivo', label: 'En Vivo', icon: 'ri-live-line' },
    { id: 'scheduled', label: 'Programadas', icon: 'ri-calendar-todo-line' },
    { id: 'finalizado', label: 'Finalizadas', icon: 'ri-history-line' },
    { id: 'recording_available', label: 'Replay', icon: 'ri-play-circle-line' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      <div className="px-4 pt-2 pb-2">
        {/* Status filters */}
        <div className="flex gap-1 flex-wrap mb-3">
          {statusFilters.map(sf => (
            <button key={sf.id} onClick={() => setLiveStatus(sf.id as typeof liveStatus)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                liveStatus === sf.id ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}>
              <i className={`${sf.icon} text-[11px]`}></i>{sf.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 py-1">
        <p className="text-[11px] text-foreground-400">{filtered.length} transmisión{filtered.length !== 1 ? 'es' : ''}</p>
      </div>
      <div className="px-3 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-live-line text-xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">Sin transmisiones</p>
          </div>
        ) : filtered.map(live => (
          <LiveTransmisionCard
            key={live.id}
            live={live}
            onOpen={() => setSelectedLive(live)}
            reminded={hasReminder(live.id, 'live')}
            onToggleReminder={() => {
              const added = toggleReminder(live.id, 'live', live.title, live.date);
              SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
            }}
          />
        ))}
      </div>

      {/* Live Detail Modal */}
      {selectedLive && (() => {
        const live = selectedLive;
        const sCol = liveStatusColors[live.status];
        const pCol = providerColors[live.provider];
        const reminded = hasReminder(live.id, 'live');
        const dateLabel = new Date(live.date).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

        return (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedLive(null)}></div>
            <div className="relative bg-background-50 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-[slideUp_0.2s_ease-out]">
              <button onClick={() => setSelectedLive(null)} className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-foreground-700"></i>
              </button>
              <div className="relative h-44 overflow-hidden rounded-t-3xl">
                <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold ${sCol.bg} ${sCol.text}`}>{live.status === 'en_vivo' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>}{liveStatusLabels[live.status]}</span></div>
                <div className="absolute bottom-3 left-3 right-3"><h3 className="text-lg font-extrabold text-white leading-tight">{live.title}</h3></div>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-sm text-foreground-700 leading-relaxed">{live.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2.5 rounded-xl bg-background-100"><p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Fecha</p><p className="text-xs font-bold text-foreground-900 capitalize">{dateLabel}</p></div>
                  <div className="p-2.5 rounded-xl bg-background-100"><p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Horario</p><p className="text-xs font-bold text-foreground-900">{live.time}</p></div>
                  <div className="p-2.5 rounded-xl bg-background-100"><p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Locutor</p><p className="text-xs font-bold text-foreground-900">{live.host}</p></div>
                  <div className="p-2.5 rounded-xl bg-background-100"><p className="text-[10px] text-foreground-400 uppercase font-semibold mb-0.5">Plataforma</p><p className={`text-xs font-bold flex items-center gap-1 ${pCol.text}`}><i className={`${providerIcons[live.provider]} text-[11px]`}></i>{providerLabels[live.provider]}</p></div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-background-100">
                  <button onClick={() => {
                    const url = `${window.location.origin}/live/${live.id}`;
                    if (navigator.share) navigator.share({ title: live.title, text: live.description.slice(0, 100), url });
                    else { navigator.clipboard.writeText(url); SwalAlert.toast('Link copiado', 'success'); }
                  }} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-[11px]"></i>Compartir
                  </button>
                  <button onClick={() => {
                    const added = toggleReminder(live.id, 'live', live.title, live.date);
                    SwalAlert.toast(added ? 'Recordatorio activado' : 'Recordatorio cancelado', added ? 'success' : 'info');
                  }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      reminded ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}>
                    <i className={`text-[11px] ${reminded ? 'ri-notification-3-fill' : 'ri-notification-3-line'}`}></i>
                    {reminded ? 'Recordado' : 'Recordarme'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ── Main Tab ── */
interface AppLiveTabProps {
  isActive: boolean;
  onPlayStation: (station: any) => void;
}

export default function AppLiveTab({ isActive, onPlayStation }: AppLiveTabProps) {
  const navigate = useNavigate();
  const [section, setSection] = useState<LiveSection>('radios');
  const [radioSort, setRadioSort] = useState<SortMode>('activity');
  const [programSort, setProgramSort] = useState<ProgramSortMode>('activity');
  const [hostSort, setHostSort] = useState<HostSortMode>('activity');

  const sortedRadios = useMemo(() => {
    const list = [...liveRadios];
    if (radioSort === 'activity') list.sort((a, b) => b.activityScore - a.activityScore);
    else if (radioSort === 'listeners') list.sort((a, b) => b.currentListeners - a.currentListeners);
    else list.sort((a, b) => b.interactionScore - a.interactionScore);
    return list;
  }, [radioSort]);

  const sortedPrograms = useMemo(() => {
    const list = [...livePrograms];
    if (programSort === 'activity') list.sort((a, b) => b.activityScore - a.activityScore);
    else if (programSort === 'interactions') list.sort((a, b) => b.interactions - a.interactions);
    else list.sort((a, b) => b.followers - a.followers);
    return list;
  }, [programSort]);

  const sortedHosts = useMemo(() => {
    const list = [...liveHosts];
    if (hostSort === 'activity') list.sort((a, b) => b.activityScore - a.activityScore);
    else if (hostSort === 'followers') list.sort((a, b) => b.followers - a.followers);
    else list.sort((a, b) => a.name.localeCompare(b.name, 'es'));
    return list;
  }, [hostSort]);

  const totalListeners = useMemo(() => liveRadios.reduce((s, r) => s + r.currentListeners, 0), []);

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      {/* Header */}
      <div className="px-4 md:px-6 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-100 to-rose-100 flex items-center justify-center">
            <i className="ri-live-fill text-red-500 text-lg"></i>
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-foreground-950">En Vivo</h1>
            <p className="text-[11px] text-foreground-400">
              {liveRadios.length} radios · {livePrograms.length} programas · {liveHosts.length} locutores · {formatCount(totalListeners)} oyentes
            </p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-[10px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
            LIVE
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-none">
          {LIVE_SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                section === s.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${s.icon} ${section === s.id ? 'text-background-50' : s.color} text-[13px]`}></i>
              {s.label}
              <span className={`text-[10px] ${section === s.id ? 'text-background-50/70' : 'text-foreground-400'}`}>{s.count}</span>
            </button>
          ))}
        </div>

        {/* Sort pills */}
        {section !== 'transmisiones' && (
          <div className="flex gap-1.5 mb-3">
            {section === 'radios' && RADIO_SORTS.map((srt) => (
              <button key={srt.key} onClick={() => setRadioSort(srt.key)}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  radioSort === srt.key ? 'bg-rose-100 text-rose-700' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                }`}>{srt.label}</button>
            ))}
            {section === 'programs' && PROGRAM_SORTS.map((srt) => (
              <button key={srt.key} onClick={() => setProgramSort(srt.key)}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  programSort === srt.key ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                }`}>{srt.label}</button>
            ))}
            {section === 'hosts' && HOST_SORTS.map((srt) => (
              <button key={srt.key} onClick={() => setHostSort(srt.key)}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  hostSort === srt.key ? 'bg-amber-100 text-amber-700' : 'bg-background-100 text-foreground-400 hover:bg-background-200'
                }`}>{srt.label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-3 md:px-5 pb-6">
        {section === 'transmisiones' && <TransmisionesSection />}

        {section === 'radios' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sortedRadios.map((radio) => {
              const station = radioStations.find((s) => s.name === radio.name);
              const slug = station?.slug || radio.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
              return <LiveRadioCard key={radio.id} radio={radio} onClick={() => navigate(`/radio/${slug}`)} />;
            })}
          </div>
        )}

        {section === 'programs' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedPrograms.map((program) => <LiveProgramCard key={program.id} program={program} onClick={() => navigate(`/program/${program.slug}`)} />)}
          </div>
        )}

        {section === 'hosts' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sortedHosts.map((host) => <LiveHostCard key={host.id} host={host} onClick={() => navigate(`/host/${host.name.toLowerCase().replace(/\s+/g, '-')}`)} />)}
          </div>
        )}
      </div>
    </div>
  );
}